import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers, deleteUser } from "./usersSlice";
import { Edit, Trash2, Search, Loader2, Plus, Funnel } from "lucide-react";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import useDocumentTitle from "../../hooks/useDocumentTitle";

export default function UserList() {
  const dispatch = useDispatch();
  const { users, status, error, page, hasMore } = useSelector((state) => state.users);
  useDocumentTitle("Franchisee List");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState({
    keyword: "",
  });

  const debouncedSearch = useCallback(
    debounce((data) => {
      dispatch(fetchUsers({ filterData: data, page: 1 }));
    }, 500),
    [dispatch]
  );
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    dispatch(fetchUsers({ filterData, page: 1 }));
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;

    setFilterData((prev) => {
      const updated = { ...prev, keyword: value };
      debouncedSearch(updated);
      return updated;
    });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      dispatch(deleteUser(id))
        .unwrap()
        .then(() => toast.success("User deleted successfully"))
        .catch(() => toast.error("Failed to delete user"));
    }
  };

  const toggleFilters = () => setShowFilters(!showFilters);

  return (
    <div className="bg-gray-100 min-h-screen text-gray-800 relative">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 space-y-6">
        {/* Header */}
        <div className="bg-white shadow-md rounded-lg p-4 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">User Management</h3>

            <div className="flex items-center space-x-3">
              {/* Filters Button */}
              <button
                onClick={toggleFilters}
                className={`px-5 py-2 rounded font-medium shadow transition flex items-center gap-2 ${
                  showFilters ? "bg-blue-600 text-white" : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
              >
                <Funnel size={18} />
                Filters
              </button>

              {/* Add User */}
              <Link
                to="/users/create"
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={18} />
                Add User
              </Link>
            </div>
          </div>
        </div>

        {/* FILTER PANEL */}
        {showFilters && (
          <div className="bg-white shadow-md rounded-lg p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Search User</label>
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={filterData.keyword}
                    onChange={handleSearchChange}
                    className="pl-10 pr-4 py-2 w-full border rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowFilters(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
              >
                Close Filters
              </button>
            </div>
          </div>
        )}

        {/* ====================== TABLE CARD ====================== */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-2">
          <div className="overflow-x-auto">
            <table className="min-w-full border text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 border text-left">Name</th>
                  <th className="px-3 py-2 border text-left">Email</th>
                  <th className="px-3 py-2 border text-left">Actions</th>
                </tr>
              </thead>

              <tbody>
                {status === "loading" && page === 1 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center">
                      <Loader2 className="animate-spin text-blue-600 mx-auto" />
                    </td>
                  </tr>
                ) : users.data && users.data.length > 0 ? (
                  users.data.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 border">{user.name}</td>
                      <td className="px-3 py-2 border">{user.email}</td>
                      <td className="px-3 py-2 border">
                        <div className="flex gap-4">
                          <Link
                            to={`/users/edit/${btoa(user.id.toString())}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit size={18} />
                          </Link>
                          <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Loader for more pages */}
            {status === "loading" && page > 1 && (
              <div className="flex justify-center my-4">
                <Loader2 className="animate-spin text-blue-600" size={24} />
              </div>
            )}

            {/* End message */}
            {!hasMore && users.data?.length > 0 && (
              <div className="text-center py-4 text-gray-500">No more users to load</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
